import { create } from 'zustand'
import type { RestaurantTable } from '@/types'
import { tableService } from '@/lib/table'
import { toast } from 'sonner'

interface TableState {
  tables: RestaurantTable[]
  selectedTable: RestaurantTable | null
  isLoading: boolean
  error: string | null

  // Actions
  fetchTables: (restaurantId: string, activeOnly?: boolean) => Promise<void>
  fetchTableById: (tableId: string) => Promise<RestaurantTable>
  createTable: (restaurantId: string, data: Omit<RestaurantTable, 'id' | 'restaurantId' | 'qrCodeData' | 'qrCodeUrl' | 'createdAt' | 'updatedAt'>) => Promise<RestaurantTable>
  updateTable: (tableId: string, data: Partial<RestaurantTable>) => Promise<RestaurantTable>
  toggleTableStatus: (tableId: string) => Promise<void>
  regenerateQRCode: (tableId: string) => Promise<void>
  deleteTable: (tableId: string) => Promise<void>
  setSelectedTable: (table: RestaurantTable | null) => void
  clearError: () => void
}

export const useTableStore = create<TableState>((set, get) => ({
  tables: [],
  selectedTable: null,
  isLoading: false,
  error: null,

  // ========== Fetch Operations ==========
  fetchTables: async (restaurantId: string, activeOnly = false) => {
    set({ isLoading: true, error: null })
    try {
      const tables = await tableService.getTables(restaurantId, activeOnly)
      set({ tables, isLoading: false })
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Errore nel caricamento tavoli'
      set({ error: errorMessage, isLoading: false })
      toast.error(errorMessage)
      throw error
    }
  },

  fetchTableById: async (tableId: string) => {
    set({ isLoading: true, error: null })
    try {
      const table = await tableService.getTableById(tableId)
      set({ selectedTable: table, isLoading: false })
      return table
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Errore nel caricamento tavolo'
      set({ error: errorMessage, isLoading: false })
      toast.error(errorMessage)
      throw error
    }
  },

  // ========== Table CRUD ==========
  createTable: async (restaurantId: string, data) => {
    set({ isLoading: true, error: null })
    try {
      const newTable = await tableService.createTable(restaurantId, data)
      set(state => ({
        tables: [...state.tables, newTable].sort((a, b) =>
          a.tableNumber.localeCompare(b.tableNumber, undefined, { numeric: true })
        ),
        isLoading: false
      }))
      toast.success('Tavolo creato con successo')
      return newTable
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Errore nella creazione tavolo'
      set({ error: errorMessage, isLoading: false })
      toast.error(errorMessage)
      throw error
    }
  },

  updateTable: async (tableId: string, data) => {
    set({ isLoading: true, error: null })
    try {
      const updatedTable = await tableService.updateTable(tableId, data)
      set(state => ({
        tables: state.tables
          .map(t => t.id === tableId ? updatedTable : t)
          .sort((a, b) => a.tableNumber.localeCompare(b.tableNumber, undefined, { numeric: true })),
        selectedTable: state.selectedTable?.id === tableId ? updatedTable : state.selectedTable,
        isLoading: false
      }))
      toast.success('Tavolo aggiornato con successo')
      return updatedTable
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Errore nell\'aggiornamento tavolo'
      set({ error: errorMessage, isLoading: false })
      toast.error(errorMessage)
      throw error
    }
  },

  toggleTableStatus: async (tableId: string) => {
    try {
      const updatedTable = await tableService.toggleTableStatus(tableId)
      set(state => ({
        tables: state.tables.map(t => t.id === tableId ? updatedTable : t),
        selectedTable: state.selectedTable?.id === tableId ? updatedTable : state.selectedTable,
      }))
      toast.success(`Tavolo ${updatedTable.active ? 'attivato' : 'disattivato'}`)
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Errore nel cambio stato'
      toast.error(errorMessage)
      throw error
    }
  },

  regenerateQRCode: async (tableId: string) => {
    try {
      const updatedTable = await tableService.regenerateQRCode(tableId)
      set(state => ({
        tables: state.tables.map(t => t.id === tableId ? updatedTable : t),
        selectedTable: state.selectedTable?.id === tableId ? updatedTable : state.selectedTable,
      }))
      toast.success('Codice QR rigenerato')
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Errore nella rigenerazione QR'
      toast.error(errorMessage)
      throw error
    }
  },

  deleteTable: async (tableId: string) => {
    set({ isLoading: true, error: null })
    try {
      await tableService.deleteTable(tableId)
      set(state => ({
        tables: state.tables.filter(t => t.id !== tableId),
        selectedTable: state.selectedTable?.id === tableId ? null : state.selectedTable,
        isLoading: false
      }))
      toast.success('Tavolo eliminato con successo')
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Errore nell\'eliminazione tavolo'
      set({ error: errorMessage, isLoading: false })
      toast.error(errorMessage)
      throw error
    }
  },

  // ========== UI State ==========
  setSelectedTable: (table) => set({ selectedTable: table }),
  clearError: () => set({ error: null }),
}))
